import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, LayoutGrid, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, SurfaceTab, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { CompanyBrainPillar } from '../components/alt/CompanyBrainPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';

// Order carries the argument: connect the tools, build with them, take that
// intelligence to where people work, and let it compound.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'platform',
  label: 'Platform',
  icon: LayoutGrid
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  useEffect(() => {
    const sections = SECTIONS.map(section => document.getElementById(section.id)).filter((section): section is HTMLElement => Boolean(section));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const scrollingDown = window.scrollY >= lastScrollYRef.current;
      lastScrollYRef.current = window.scrollY;
      const entering = entries.filter(entry => entry.isIntersecting).sort((a, b) => SECTIONS.findIndex(section => section.id === a.target.id) - SECTIONS.findIndex(section => section.id === b.target.id));
      const next = scrollingDown ? entering[entering.length - 1] : entering[0];
      if (next?.target.id) {
        setActiveSection(next.target.id as SectionId);
      }
    }, {
      rootMargin: '-32% 0px -67% 0px',
      threshold: 0
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-6028" data-mp="1wslfek82k0ve">
      <section className="pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-6029" data-mp="cdjcchc6rf0w">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6030" data-mp="cf2y2ngm2ia2">
          <div className="mx-auto max-w-5xl text-center" data-id="element-6031" data-mp="2zknu68jtzyfq">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-6032" data-mp="34ka86k7hhipl">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-6033" data-mp="39k51svin8mec">
                <AnimatePresence mode="wait" initial={false} data-id="element-6034" data-mp="3dk7333kgvdt3">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-6035" data-mp="2gv7svj0bo1y9">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-6036" data-mp="1uk6eo7pne7lq" />
              that want to <HeadlineIcon icon={Zap} data-id="element-6037" data-mp="1pn7mvvtgicqp" />
              <UnderscoredPhrase data-id="element-6038" data-mp="1po8xy0ah2z8f">Go Faster</UnderscoredPhrase>
              <br data-id="element-6039" data-mp="1ll2uf0eqvm5n" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-6040" data-mp="1lkaytpl5dofd" />
              <UnderscoredPhrase data-id="element-6041" data-mp="1gn1dal0vpwwt">Stay in Control</UnderscoredPhrase>
            </h1>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-6042" data-mp="1bnya764gm0rh">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-6043" data-mp="16wyaxu7vb6lt">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-6044" data-mp="sxq90tkblkdx">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-6045" data-mp="9bnbigtu58d3" />
                <span className="min-w-0 leading-tight" data-id="element-6046" data-mp="x1rbme7tepzc">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-6047" data-mp="nbfx5xqi8ye7">
        <section id="connected-apps" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-6048" data-mp="nbpqnvardnon">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6049" data-mp="s8y9zz7n7xrh">
            <ConnectedAppsPillar data-id="element-6050" data-mp="1g03lk26jw3vz" />
            <div className="mt-20 md:mt-28" data-id="element-6051" data-mp="1fz21x9bgy92n">
              <h3 className="mb-8 font-title text-3xl font-bold leading-tight tracking-tight text-[#2B211A] md:text-4xl" data-id="element-6052" data-mp="1kqk1bsxug1li">
                Why not just use OpenAI or Anthropic?
              </h3>
              <ConnectedAppsTable data-id="element-6053" data-mp="35emhx2mond27" />
            </div>
          </div>
        </section>

        <section id="platform" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-6054" data-mp="30frypzkszmhk">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6055" data-mp="3f8livyb0sqsk">
            <PlatformPillar data-id="element-6056" data-mp="3a87h2y00id2z" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-6057" data-mp="2mhlcjgp7vaxq">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6058" data-mp="2hk1tm3hkq9zg">
            <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-6059" data-mp="2viji43fazqif">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-6060" data-mp="2vgggsn8paaye">
                Everywhere your team works
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-6061" data-mp="ar95zt0wi7o6">
                Your AI doesn't have to
                <br data-id="element-6062" data-mp="209y0bcjanns4" />
                live in another app.
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-6063" data-mp="207v0y1w94iaq">
                Everything your company builds in Giga can show up as a co
                worker in Slack, ChatGPT, and Claude Code — with Teams and
                iMessage next. Same connections, same brain, no new tab.
              </p>
            </div>

            <div className="grid items-center gap-10 md:grid-cols-[1.2fr_0.8fr]" data-id="element-6064" data-mp="2ize1m22szk9l">
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-6065" data-mp="2nyh6nmis9qhk" />

              <div data-id="element-6066" data-mp="2sy3n04twrmul">
                <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden md:flex" tabClassName="!px-4 !py-2.5 !text-base" data-id="element-6067" data-mp="2szoflownwvpt" />

                <div className="space-y-4 font-title text-xl text-[#2B211A] md:text-2xl" style={{
                fontWeight: 500
              }} data-id="element-6068" data-mp="yl415tyxpwev">
                  <IconBullet icon={PlugZap} label="Reads and writes in your connected tools" data-id="element-6069" data-mp="13ljckfznotu7" />
                  <IconBullet icon={Zap} label="Real work: audits, analysis, reports, presentations" data-id="element-6070" data-mp="3gvt5mg0tyr7y" />
                  <IconBullet icon={Sparkles} label="Autonomous and proactive" data-id="element-6071" data-mp="3c0vp6o181fl1" />
                </div>
                <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-6072" data-mp="37zabx1kryzab">
                  The co worker is one interface into the platform — not a
                  separate product with its own setup to maintain.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 bg-white py-20 md:py-28" data-id="element-6073" data-mp="3qrmenpfcc2kz">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6074" data-mp="3lsryywco6u53">
            <CompanyBrainPillar data-id="element-6075" data-mp="3ls9whhzezjoq" />
            <div className="mt-20 border-t border-[#EFEAE1] pt-20 md:mt-28 md:pt-28" data-id="element-6076" data-mp="2fcpna8vop1ag">
              <BrainFlowExperiment data-id="element-6077" data-mp="2facx58frvkmb" />
            </div>
          </div>
        </section>

        <section className="bg-[#FAF8F4] py-20 md:py-28" data-id="element-6078" data-mp="2acbh6wdn81f2">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6079" data-mp="20tvojsj35x83">
            <AccelerationControlSection data-id="element-6080" data-mp="1vvlxnu4e9isa" />
            <ClosingCTA data-id="element-6081" data-mp="1vwnao44z0fcn" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="mr-[0.18em] inline-flex size-[0.86em] -translate-y-[0.06em] items-center justify-center rounded-[0.2em] bg-[#EA580C] align-middle" data-id="element-6082" data-mp="25rx96hw5u4lo">
      <Icon className="size-[0.56em] text-white" strokeWidth={2.6} data-id="element-6083" data-mp="2elk11wx8ynpl" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="relative inline-block align-baseline" data-id="element-6084" data-mp="2ojztq4bxa517">
      <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-6085" data-mp="2oif14k964w5z" />
      <span className="relative z-10" data-id="element-6086" data-mp="z763fkqcal8u">{children}</span>
    </span>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-6087" data-mp="3asy700jwolu8" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6088" data-mp="3110xqzcgk3y5">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-6089" data-mp="2r4phjy9qytyf" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-6090" data-mp="2d5pomef7bswg">
          <MoreThanAnswers embedded data-id="element-6091" data-mp="2i63sdk9rsgoo" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6092" data-mp="bbqkcd0hybt9">
        {mobileTabs}
        <CodeTerminal data-id="element-6093" data-mp="ewigr62wi5sz" />
      </div>;
  }
  return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-6094" data-mp="et4fk15z42n4">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-6095" data-mp="4x1d0u77m1zn" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-6096" data-mp="3w4noh90678r2">
        <SlackPanel data-id="element-6097" data-mp="xkx8jvl1lnuu" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6098" data-mp="1ge163s1dskuq" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-6099" data-mp="1jv5l2kbm7iw3" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6100" data-mp="1ny21c4don2wv" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-6101" data-mp="1xu5ee5a2olyl">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-6102" data-mp="26utrmxrn6iaf">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  return <div className={`w-full items-center gap-2.5 md:gap-3 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-6103" data-mp="2qggb34uk62q4">
      <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-6104" data-mp="3ib4lo2v98hrr">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6105" data-mp="uwf6bd0l30v" />
        Slack
      </SurfaceTab>
      <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" className={tabClassName} data-id="element-6106" data-mp="3s1pns0a504ff">
        <TabIcon slug="claude" fallback="C" data-id="element-6107" data-mp="2zcs721lkqfos" />
        Claude
      </SurfaceTab>
      <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-6108" data-mp="3dc1n919s59sr">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6109" data-mp="39a6ni17hz4h3" />
        ChatGPT
      </SurfaceTab>
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-6110" data-mp="27hr7rbfw96p3">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-6111" data-mp="23eukh7fesmet" data-mpi={groupIndex}>
          <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-6112" data-mp="1yg9sfvab991g">
            {group.heading}
          </h2>
          <div className="mt-8 border-y border-neutral-200" data-id="element-6113" data-mp="1thor1sjbojbf">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-6114" data-mp="1opeuvurm6n0p" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-6115" data-mp="3he6038m4tlr4">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-6116" data-mp="3mfvemy8kchjy">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  return <div className="mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center md:mt-28 md:px-16 md:py-20" data-id="element-6117" data-mp="2yo6jla4cxezb">
      <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-[52px]" data-id="element-6118" data-mp="33mrfixcktchy">
        One platform to run your company's AI on.
      </h2>
      <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-6119" data-mp="3cg4jqvg3c0jc">
        Connect your tools, build your workflows, put a co worker where your
        team already is, and let the brain get sharper every week.
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-6120" data-mp="2jxquhk8tlmvv">
        <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-6121" data-mp="1dcr4xk728cdf">
          Get started for free
          <ArrowRight size={17} data-id="element-6122" data-mp="1nbqh3cr1aq56" />
        </Link>
        <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-colors hover:border-white/60" data-id="element-6123" data-mp="1ibc7hq9sb851">
          Book a demo
        </Link>
      </div>
      <p className="mt-6 text-[13.5px] text-white/45" data-id="element-6124" data-mp="1rapf0declmn4">
        $100 in credits included · no card needed
      </p>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-6125" data-mp="r260vuwqr2wl">
      <div className="overflow-x-auto" data-id="element-6126" data-mp="zvj35ngp3gvc">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-6127" data-mp="zuhiqsdshi0y">
          <thead data-id="element-6128" data-mp="vtxivh1u00b4">
            <tr className="border-b border-neutral-200" data-id="element-6129" data-mp="vvh1heaymng2">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6130" data-mp="84bl8y13xt58">
                <span className="sr-only" data-id="element-6131" data-mp="86pfj4nyr1bq">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6132" data-mp="36tjpspf6g79">
                ChatGPT
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6133" data-mp="c5orh9843yco">
                Claude
              </th>
              <th scope="col" className="w-[18%] bg-[#FFF8F3] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6134" data-mp="1smj75yf10kn0">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6135" data-mp="laa87z9wg7lp">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-6136" data-mp="q5pgmbp51baq">
                <th scope="row" className="px-5 py-3.5 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6137" data-mp="u9owcs2lg631">
                  <span className="block" data-id="element-6138" data-mp="z76vxruq18yd">{row.feature}</span>
                  <span className="mt-1 block max-w-xl text-[12.5px] font-normal leading-[1.45] text-[#8A7B70]" data-id="element-6139" data-mp="2hzg90gs9u2d">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6140" data-mp="bhm8owrjbpja">
                  <ComparisonValue value={row.chatgpt} data-id="element-6141" data-mp="bg1hnh4fioq6" />
                </td>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6142" data-mp="gafdfkn06xvd">
                  <ComparisonValue value={row.anthropic} data-id="element-6143" data-mp="3eze4yncpevna" />
                </td>
                <td className="bg-[#FFF8F3] px-4 py-3.5 text-[#2B211A]" data-id="element-6144" data-mp="3jxxsgs6y4fhr">
                  <ComparisonValue value={row.giga} highlight data-id="element-6145" data-mp="3jvuvtpbafo42" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-6146" data-mp="32i3g0j5zdfri">
        <Check size={19} strokeWidth={2.6} aria-hidden="true" data-id="element-6147" data-mp="2tfaqakcg5c3s" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-6148" data-mp="2ookil0gjsx5x">
        <X size={19} strokeWidth={2.4} aria-hidden="true" data-id="element-6149" data-mp="2jp7xol98m05z" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-6150" data-mp="2erfu2tz8cply">—</span>;
  }
  return <span className={`text-[12px] font-semibold leading-tight ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-6151" data-mp="2aot6j59ldquk">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6152" data-mp="9o4sa1bpxpgz">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6153" data-mp="dofb4ekm0ad9">
        <Icon size={22} strokeWidth={2.4} data-id="element-6154" data-mp="dq9liyy98k33" />
      </span>
      {label}
    </p>;
}